/**
 * SelectField -- Dynamic select/dropdown field.
 *
 * Renders a shadcn/ui Select component backed by Radix UI primitives.
 * Populates options from `field.options` in the connector schema.
 * Supports label, required indicator, placeholder, help text, tooltip,
 * validation error display, and full ARIA accessibility.
 */

import { useMemo } from 'react';
import { HelpCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { declaredOptionChange } from '@/lib/declaredOptionChange';
import type { DynamicFieldProps } from './index';

export function SelectField({ field, value, onChange, error, readOnly }: DynamicFieldProps) {
  const selectId = `field-${field.name}`;
  const helpId = `${selectId}-help`;
  const errorId = `${selectId}-error`;

  const describedBy = [
    field.help_text ? helpId : null,
    error ? errorId : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  const currentValue = typeof value === 'string' ? value : '';

  // #2010 -- drop the '' Radix echoes back when the wizard's seed effect lands
  // a schema default in the commit after this field mounted, before the closed
  // content has registered its options. See declaredOptionChange.
  const handleValueChange = useMemo(
    () => declaredOptionChange(field.options, onChange),
    [field.options, onChange]
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1">
        <Label htmlFor={selectId}>
          {field.label}
          {field.required && (
            <>
              <span aria-hidden="true" className="text-destructive ml-1">*</span>
              <span className="sr-only">(required)</span>
            </>
          )}
        </Label>

        {field.tooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle
                  className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                  aria-hidden="true"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs text-sm">{field.tooltip}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <Select
        value={currentValue}
        onValueChange={handleValueChange}
        disabled={readOnly}
      >
        <SelectTrigger
          id={selectId}
          aria-required={field.required}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          className={cn(error && 'border-destructive focus:ring-destructive')}
        >
          <SelectValue placeholder={field.placeholder ?? 'Select an option'} />
        </SelectTrigger>

        <SelectContent>
          {field.options?.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {field.help_text && (
        <p id={helpId} className="text-sm text-muted-foreground">
          {field.help_text}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
